<script setup lang="ts">
import type { TenantLandingConfig, TenantBrandingConfig } from '~/types/types'

/**
 * Footer CTA banner, themed via --site-* tokens.
 *  - accent: solid accent band (default)
 *  - plain:  typographic, no band — rule above, quiet button
 */
const props = withDefaults(defineProps<{
  landing: TenantLandingConfig
  branding?: TenantBrandingConfig
  variant?: 'accent' | 'plain'
  displayClass?: string
}>(), { branding: undefined, variant: 'accent', displayClass: '' })

const isExternal = computed(() =>
  !!props.landing.cta_footer_button_url && /^(https?:|mailto:|tel:)/.test(props.landing.cta_footer_button_url))
</script>

<template>
  <section
    v-if="landing.cta_footer_headline"
    class="px-6 py-20 text-center"
    :class="variant === 'plain'
      ? 'mx-auto max-w-3xl border-t border-[var(--site-border)]'
      : 'bg-[var(--site-accent)] text-[var(--site-accent-fg)]'"
  >
    <div class="mx-auto max-w-3xl">
      <h2
        class="text-3xl sm:text-4xl tracking-tight"
        :class="displayClass"
      >
        {{ landing.cta_footer_headline }}
      </h2>
      <p
        v-if="landing.cta_footer_subtext"
        class="mt-4 text-base sm:text-lg"
        :class="variant === 'plain' ? 'text-[var(--site-muted)]' : 'opacity-90'"
      >
        {{ landing.cta_footer_subtext }}
      </p>
      <NuxtLink
        v-if="landing.cta_footer_button_text"
        :to="landing.cta_footer_button_url || '#contact'"
        :external="isExternal"
        class="mt-8 inline-block px-6 py-3 text-base font-semibold transition"
        :class="variant === 'plain'
          ? 'site-btn-accent'
          : 'rounded-[var(--site-radius)] border border-current bg-transparent hover:bg-[color-mix(in_oklab,var(--site-accent-fg)_12%,transparent)]'"
      >
        {{ landing.cta_footer_button_text }}
      </NuxtLink>
    </div>
  </section>
</template>
